import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { GAME_TURN_LIMIT } from '../../../../shared/types';
import { api, session } from '../../shell/bridge';
import { Pressable } from '../../ui/Pressable';
import { GuessMePanel } from '../../games/GuessMePanel';
import { HalfAMapPanel } from '../../games/HalfAMapPanel';
import { GuessmatePanel } from '../../games/GuessmatePanel';
import { WhereAmIPanel } from '../../games/WhereAmIPanel';
import { NocturnePanel } from '../../games/nocturne/NocturnePanel';
import {
  GAME_SECTIONS,
  gameById,
  recommendGame,
  type GameId,
  type GameTile,
} from '../../games/catalogue';
import { useGameSignals } from '../../games/signals';
/* Card psHEfEkK9cT2: the shared-win burst is asked for by round, and the
   app-wide layer paints it unless Settings has confetti off. */
import { celebrateOnce } from '../../lib/celebrate';
import './game.css';

/** Closed list — mirrors the worker's categories (card 5IXdtz7BL3CX).
 *  The three WYR modes are categories too (card DbcAi0izvJln), so they
 *  answer keep/discard and the rotation like any other game. */
const CATEGORIES: { key: string; label: string }[] = [
  { key: 'places', label: 'Places to visit' },
  { key: 'restaurants', label: 'Restaurants' },
  { key: 'would-you-rather', label: 'Would you rather' },
  { key: 'this-or-that', label: 'This or that' },
  { key: 'memory', label: 'Memory prompts' },
  { key: 'wyr-silly', label: 'Would you rather · Silly' },
  { key: 'wyr-spicy', label: 'Would you rather · Spicy' },
  { key: 'wyr-deep', label: 'Would you rather · Deep' },
];

/** Closing copy (card cNGAFvkiRCGz): time-of-day from the LOCAL clock. */
function daypart(): 'morning' | 'afternoon' | 'evening' {
  const h = new Date().getHours();
  return h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
}

interface GameRoundView {
  roundId: string;
  category: string;
  question: string;
  state: 'open' | 'revealed' | 'closed';
  answeredBy: string[];
  myAnswer: string | null;
  myPassed: boolean;
  partnerAnswer: string | null;
  partnerPassed: boolean;
  rating: 'keep' | 'discard' | null;
  /** WYR A-or-B options — null on a topic round (card DbcAi0izvJln). */
  options: [string, string] | null;
  /** Revealed rounds in the current session (card cNGAFvkiRCGz). */
  turns: number;
}

interface GamePanelProps {
  members: { memberId: string; displayName: string }[];
}

/** The back row every game view opens with — one way out, always the same. */
function BackToGames({ onBack }: { onBack: () => void }) {
  return (
    <div className="games-bar">
      <button type="button" className="games-back" id="btn-games-back" onClick={onBack}>
        <span aria-hidden="true">‹</span> All games
      </button>
    </div>
  );
}

/**
 * Back-and-forth topic round (card 5IXdtz7BL3CX) — the concierge's first
 * two-player surface. Both partners answer the same prompt; neither answer
 * shows until both have answered (or passed), then they appear side by side
 * and "next topic" starts the next round.
 *
 * Since card jT0odGfG7MVo it is one game among eight: the tab's recommendation
 * and section list sit above it, so this panel renders only once a tile has
 * opened it, on that tile's category.
 */
function BackAndForth({
  members,
  initialCategory,
  autoStart = false,
  onBack,
}: GamePanelProps & { initialCategory?: string; autoStart?: boolean; onBack: () => void }) {
  const me = session();
  const [round, setRound] = useState<GameRoundView | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [category, setCategory] = useState(initialCategory ?? 'places');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Closing card dismissed for this reveal (card cNGAFvkiRCGz) — a quiet
     exit that leaves the reveal readable. Reload brings it back. */
  const [closingHidden, setClosingHidden] = useState(false);

  const partner = members.find(m => m.memberId !== me?.memberId);
  const partnerName = partner?.displayName ?? 'your partner';
  const nameOf = (memberId: string) =>
    members.find(m => m.memberId === memberId)?.displayName ?? 'Someone';

  const reload = useCallback(async () => {
    try {
      const data = await api<{ round: GameRoundView | null }>('/api/game/round');
      setRound(data.round ?? null);
      setError(null);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        console.error('[Pear] game load failed', e);
      }
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /* "Play" on the recommendation is one tap, not two (card vsDgyzDbgtxh's
     promise, now that the recc lives on the tab): the panel opens already
     asking the server for the round it recommended. Guarded by a ref so a
     reload of an open round never opens a second one. */
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!autoStart || !loaded || round || autoStarted.current) return;
    autoStarted.current = true;
    void start(false, initialCategory ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, loaded, round]);

  // Live: the partner's answer, or their "next topic", arrives over the room
  // socket as t:'game' and useThread turns it into this event.
  useEffect(() => {
    const onGame = () => void reload();
    window.addEventListener('pear:game', onGame);
    return () => window.removeEventListener('pear:game', onGame);
  }, [reload]);

  /* catOverride: undefined = use the chip (existing behaviour), null = send
     no category so the server's rotation picks ("Play another" should feel
     fresh, not pinned to whatever chip was last lit). */
  const start = async (advance: boolean, catOverride?: string | null) => {
    if (busy) return;
    setBusy(true);
    try {
      const chosen = catOverride === null ? undefined : (catOverride ?? category);
      const data = await api<{ round: GameRoundView }>('/api/game/round', {
        body: chosen ? { category: chosen, advance } : { advance },
      });
      setRound(data.round);
      setDraft('');
      setError(null);
      setClosingHidden(false);
    } catch (e) {
      console.error('[Pear] game start failed', e);
      setError('Could Not Start a Round — Try Again.');
    } finally {
      setBusy(false);
    }
  };

  /* WYR pick (card DbcAi0izvJln): the answer IS the option string, so the
     same /api/game/answer route carries it and the server validates it
     against the round's shipped pair. */
  const pick = async (option: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await api<{ round: GameRoundView }>('/api/game/answer', { body: { text: option } });
      setRound(data.round);
      setError(null);
    } catch (e) {
      console.error('[Pear] game pick failed', e);
      setError('That Did Not Save — Try Again.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const data = await api<{ round: GameRoundView }>('/api/game/answer', { body: { text } });
      setRound(data.round);
      setDraft('');
      setError(null);
    } catch (err) {
      console.error('[Pear] game answer failed', err);
      setError('That Did Not Save — Try Again.');
    } finally {
      setBusy(false);
    }
  };

  const pass = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await api<{ round: GameRoundView }>('/api/game/pass', { body: {} });
      setRound(data.round);
      setDraft('');
    } catch (e) {
      console.error('[Pear] game pass failed', e);
    } finally {
      setBusy(false);
    }
  };

  // Keep / discard a finished round (card OJmoL0w1fGrm), couple-level.
  const rate = async (verdict: 'keep' | 'discard') => {
    if (busy || !round) return;
    setBusy(true);
    try {
      const data = await api<{ round: GameRoundView }>('/api/game/rate', {
        body: { roundId: round.roundId, verdict },
      });
      setRound(data.round);
    } catch (e) {
      console.error('[Pear] game rate failed', e);
      setError('That Did Not Save — Try Again.');
    } finally {
      setBusy(false);
    }
  };

  const iAnswered = !!round && (round.answeredBy.includes(me?.memberId ?? '') || round.myPassed);
  const partnerAnswered = !!round && round.answeredBy.some(id => id !== me?.memberId);
  const revealed = round?.state === 'revealed';
  /* WYR (card DbcAi0izvJln): an options-bearing round is the A-or-B game. */
  const isWyr = !!round?.options;
  const picked = isWyr && revealed && !round?.myPassed && !round?.partnerPassed;
  const matched = picked && round?.myAnswer != null && round.myAnswer === round.partnerAnswer;
  /* Closing card (card cNGAFvkiRCGz): at the turn limit on a revealed round,
     the closing replaces the "start/next" affordance — the session is over
     until someone chooses another one. */
  const sessionOver = revealed && !!round && round.turns >= GAME_TURN_LIMIT;

  /* Shared win (card psHEfEkK9cT2): the burst belongs to the moment BOTH of
     you land the same answer, not to opening the screen. The first sight of a
     round seeds the tracker and never celebrates — that seed is what stops a
     reload of an already-finished round from bursting again — and only a
     round that TURNS revealed while you are watching can earn it. */
  const roundSeenRef = useRef<{ id: string; revealed: boolean } | null>(null);
  const celebratedRoundRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!loaded || !round) return;
    const prev = roundSeenRef.current;
    roundSeenRef.current = { id: round.roundId, revealed: round.state === 'revealed' };
    if (!prev || prev.id !== round.roundId || prev.revealed) return;
    if (round.state !== 'revealed' || !matched) return;
    if (celebratedRoundRef.current.has(round.roundId)) return;
    celebratedRoundRef.current.add(round.roundId);
    celebrateOnce(`game-win:${round.roundId}`);
  }, [loaded, round, matched]);

  return (
    <>
      <BackToGames onBack={onBack} />

      <section className="game-panel" id="game-panel" aria-labelledby="game-title">
        <h2 className="game-title" id="game-title">
          Back and forth
        </h2>

        {!loaded && <p className="game-hint">Loading…</p>}

        {loaded && !round && (
          <>
            <p className="game-hint" id="game-empty">
              Pick a topic, you both answer, then you see each other's take. Nobody sees anything until
              both of you have written.
            </p>
            <div className="game-cats" role="group" aria-label="Topic category">
              {CATEGORIES.map(c => (
                <button
                  key={c.key}
                  type="button"
                  aria-pressed={category === c.key}
                  className={`game-cat${category === c.key ? ' is-on' : ''}`}
                  onClick={() => setCategory(c.key)}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <Pressable
              className="pear-btn pear-btn-mustard game-start"
              id="btn-game-start"
              disabled={busy}
              onClick={() => void start(false)}
            >
              Start a round
            </Pressable>
          </>
        )}

        {loaded && round && (
          <>
            <p className="game-category" id="game-category">
              {CATEGORIES.find(c => c.key === round.category)?.label ?? round.category}
            </p>
            <p className="game-question" id="game-question">
              {round.question}
            </p>

            {!revealed && (
              <>
                {iAnswered ? (
                  <p className="game-status" id="game-answered">
                    {isWyr && round.myAnswer ? `You picked: ${round.myAnswer}. ` : ''}
                    Your answer is in.{' '}
                    {partnerAnswered ? `Waiting for ${partnerName} to answer…` : `Waiting for ${partnerName} to answer.`}
                  </p>
                ) : isWyr && round.options ? (
                  /* WYR (card DbcAi0izvJln): the round ships its two options —
                     a pick, not prose. One button each, then Pass. */
                  <div className="game-wyr" role="group" aria-label="Your pick">
                    {round.options.map((opt, i) => (
                      <button
                        key={opt}
                        type="button"
                        className="pear-btn game-wyr-opt"
                        id={`game-wyr-${i === 0 ? 'a' : 'b'}`}
                        disabled={busy}
                        onClick={() => void pick(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="pear-btn game-pass"
                      id="btn-game-pass"
                      disabled={busy}
                      onClick={() => void pass()}
                    >
                      Pass
                    </button>
                  </div>
                ) : (
                  <form className="game-answer" onSubmit={submit}>
                    <input
                      id="game-answer"
                      className="pear-chat-input game-input"
                      placeholder="Your answer…"
                      aria-label="Your answer"
                      value={draft}
                      onChange={e => setDraft(e.target.value)}
                    />
                    <div className="game-actions">
                      <button
                        type="submit"
                        className="pear-btn pear-btn-mustard"
                        id="btn-game-answer"
                        disabled={busy || draft.trim().length === 0}
                      >
                        Send
                      </button>
                      <button
                        type="button"
                        className="pear-btn game-pass"
                        id="btn-game-pass"
                        disabled={busy}
                        onClick={() => void pass()}
                      >
                        Pass
                      </button>
                    </div>
                  </form>
                )}
                {!iAnswered && partnerAnswered && (
                  <p className="game-status" id="game-waiting">
                    {partnerName} has answered — your turn.
                  </p>
                )}
              </>
            )}

            {revealed && (
              <div className="game-reveal" id="game-reveal">
                <div className="game-side">
                  <span className="game-who">{me?.memberId ? nameOf(me.memberId) : 'You'}</span>
                  <p className="game-line" id="game-mine">
                    {round.myPassed ? 'Passed' : (round.myAnswer ?? '—')}
                  </p>
                </div>
                <div className="game-side">
                  <span className="game-who">{partnerName}</span>
                  <p className="game-line" id="game-partner">
                    {round.partnerPassed ? 'Passed' : (round.partnerAnswer ?? '—')}
                  </p>
                </div>
              </div>
            )}

            {/* WYR reveal verdict (card DbcAi0izvJln): a match is the whole
                point of the round — say it plainly. A pass leaves no verdict. */}
            {revealed && picked && (
              <p className={`game-match${matched ? ' is-match' : ''}`} id="game-match" data-match={String(!!matched)}>
                {matched ? 'Same pick — you matched.' : 'Different picks — that is half the fun.'}
              </p>
            )}

            {/* Closing card (card cNGAFvkiRCGz): the session's last turn is
                revealed, so the closing copy takes the place of the next-round
                button. "Play another" resets the counter server-side by opening
                a fresh round; "Not now" folds the card away for this reveal. */}
            {sessionOver && !closingHidden && (
              <div className="game-closing" id="game-closing" data-test="game-closing">
                <p className="game-closing-copy">
                  That concludes this round. Feel free to play another, and enjoy the rest of your{' '}
                  {daypart()} together.
                </p>
                <div className="game-closing-actions">
                  <button
                    type="button"
                    className="pear-btn pear-btn-mustard"
                    id="btn-game-another"
                    disabled={busy}
                    onClick={() => void start(true, null)}
                  >
                    Play another
                  </button>
                  <button
                    type="button"
                    className="pear-btn game-pass"
                    id="btn-game-closing-dismiss"
                    onClick={() => setClosingHidden(true)}
                  >
                    Not now
                  </button>
                </div>
              </div>
            )}

            {revealed && (
              <div className="game-rating" id="game-rating">
                {round.rating === null ? (
                  <>
                    <p className="game-rating-prompt">Was this one a keeper?</p>
                    <div className="game-rating-actions">
                      <button
                        type="button"
                        id="btn-game-keep"
                        className="pear-btn game-rating-btn"
                        disabled={busy}
                        onClick={() => void rate('keep')}
                      >
                        + Keep this game
                      </button>
                      <button
                        type="button"
                        id="btn-game-discard"
                        className="pear-btn game-rating-btn game-rating-discard"
                        disabled={busy}
                        onClick={() => void rate('discard')}
                      >
                        − Not for us
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="game-rating-done" id="game-rating-done">
                    {round.rating === 'keep'
                      ? `Keeping this one — it will come around again.`
                      : `Noted — we will stop offering this one.`}
                  </p>
                )}
              </div>
            )}

            {/* The closing card IS the next-round affordance at the turn limit
                (card cNGAFvkiRCGz) — two buttons for one job would read as a
                choice the product does not offer. */}
            {!(sessionOver && !closingHidden) && (
              <div className="game-actions">
                <Pressable
                  className="pear-btn pear-btn-blush"
                  id="btn-game-next"
                  disabled={busy}
                  onClick={() => void start(true)}
                >
                  Next topic
                </Pressable>
              </div>
            )}
          </>
        )}

        {error && (
          <p className="game-error" role="alert" id="game-error">
            {error}
          </p>
        )}
      </section>
    </>
  );
}

/** One game, opened from its tile. Only the opened game mounts — the old tab
 *  rendered six panels, and their fetches, at once. */
function GameView({
  tile,
  members,
  autoStart = false,
  onBack,
}: GamePanelProps & { tile: GameTile; autoStart?: boolean; onBack: () => void }) {
  switch (tile.id) {
    case 'back-and-forth':
    case 'would-you-rather':
    case 'this-or-that':
      return (
        <BackAndForth
          members={members}
          initialCategory={tile.category}
          autoStart={autoStart}
          onBack={onBack}
        />
      );
    case 'guess-me':
      return (
        <>
          <BackToGames onBack={onBack} />
          <GuessMePanel members={members} />
        </>
      );
    case 'half-a-map':
      return (
        <>
          <BackToGames onBack={onBack} />
          <HalfAMapPanel members={members} />
        </>
      );
    case 'where-am-i':
      return (
        <>
          <BackToGames onBack={onBack} />
          <WhereAmIPanel members={members} />
        </>
      );
    case 'guessmate':
      return (
        <>
          <BackToGames onBack={onBack} />
          <GuessmatePanel members={members} />
        </>
      );
    case 'nocturne':
      return (
        <>
          <BackToGames onBack={onBack} />
          <NocturnePanel members={members} />
        </>
      );
  }
}

/**
 * The intro every game entry lands on first (card aduRfcsTSaXW, founder email
 * 2026-10-31). Opening a game used to drop straight into question one: no
 * moment to read what it is, no way out but back, and a half-played game
 * indistinguishable from a fresh one. This is that moment — what it is, how
 * long, who goes first — with exactly one primary action and one way back.
 *
 * A started game reaches this screen only from Pear recommends, and its primary
 * action says so ("Take your turn" when the move is mine, "Pick up where you
 * left off" when it is theirs) instead of pretending to be a new game.
 */
function GameIntroView({
  tile,
  waiting,
  started,
  onBack,
  onPlay,
}: {
  tile: GameTile;
  waiting: boolean;
  started: boolean;
  onBack: () => void;
  onPlay: () => void;
}) {
  const primary = waiting
    ? 'Take your turn'
    : started
      ? 'Pick up where you left off'
      : 'Play';
  return (
    <div className="games-intro" id="games-intro" data-test="games-intro" data-game={tile.id}>
      <BackToGames onBack={onBack} />
      <div className="games-intro-card">
        <h2 className="games-intro-title" id="games-intro-title">
          {tile.title}
          {started && (
            <span className="games-waiting" id="games-intro-started">
              In progress
            </span>
          )}
        </h2>
        <p className="games-intro-what" id="games-intro-what">
          {tile.intro.what}
        </p>
        <dl className="games-intro-facts">
          <div className="games-intro-fact">
            <dt>How long</dt>
            <dd id="games-intro-minutes">{tile.intro.minutes}</dd>
          </div>
          <div className="games-intro-fact">
            <dt>Who goes first</dt>
            <dd id="games-intro-first">{tile.intro.first}</dd>
          </div>
        </dl>
        <Pressable
          className="pear-btn pear-btn-mustard games-intro-play"
          id="btn-games-intro-play"
          onClick={onPlay}
        >
          {primary}
        </Pressable>
      </div>
    </div>
  );
}

/**
 * The Games tab (card jT0odGfG7MVo, founder email 2026-10-31).
 *
 * Opens on one recommendation and three short section rows instead of six
 * full-height panels. The recommendation is the only thing that knows about
 * play history: a game the partner has started wins outright, otherwise the
 * day's pick comes from games the couple has neither discarded nor already
 * rated keep — the verdicts the rating card (OJmoL0w1fGrm) already records,
 * not a second signal.
 */
export function GamePanel({
  members,
  openIntent = null,
}: GamePanelProps & { openIntent?: { id: GameId; n: number } | null }) {
  const [open, setOpen] = useState<GameId | null>(null);
  const [intro, setIntro] = useState<GameId | null>(null);
  /* Whether the entry point that opened the intro also asked for a round to
     start. Only Pear-recommends' Play did before the intro existed, so only it
     still does: a tile keeps the game's own first screen (card aduRfcsTSaXW
     adds a step, it does not remove one). */
  const [introAuto, setIntroAuto] = useState(false);
  const [autoStart, setAutoStart] = useState(false);
  const [ratings, setRatings] = useState<Record<string, string> | null>(null);
  const { waiting, started } = useGameSignals(open === null);

  useEffect(() => {
    if (open !== null) return;
    let live = true;
    void (async () => {
      try {
        const given = await api<{ ratings: Record<string, string> }>('/api/game/ratings');
        if (live) setRatings(given.ratings ?? {});
      } catch (e) {
        if (!(e instanceof DOMException && e.name === 'AbortError')) {
          console.error('[Pear] game ratings load failed', e);
        }
        if (live) setRatings({});
      }
    })();
    return () => {
      live = false;
    };
  }, [open]);

  /* A thread card that names its game is a deep link, not a browse: it opens
     that game directly, which is the promise card Bkt2clm8SSOI verified (the
     card lands you on the panel that answers it). The intro governs the tab's
     own entry points — the recommendation and the tiles (card aduRfcsTSaXW).
     `n` is the re-fire key. */
  const intentN = openIntent?.n ?? 0;
  useEffect(() => {
    if (openIntent) setOpen(openIntent.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intentN]);

  const opened = open ? gameById(open) : undefined;
  if (opened) {
    return (
      <GameView
        tile={opened}
        members={members}
        autoStart={autoStart}
        onBack={() => {
          setAutoStart(false);
          setOpen(null);
        }}
      />
    );
  }

  const introTile = intro ? gameById(intro) : undefined;
  if (introTile) {
    return (
      <GameIntroView
        tile={introTile}
        waiting={!!waiting[introTile.id]}
        started={!!started[introTile.id]}
        onBack={() => setIntro(null)}
        onPlay={() => {
          /* One primary action: the game opens. Auto-start belongs to the entry
             point that asked for it, not to the tile. */
          setAutoStart(introAuto);
          setIntro(null);
          setOpen(introTile.id);
        }}
      />
    );
  }

  const recc = recommendGame(ratings, started, waiting);
  const waitingName = recc && waiting[recc.id] ? true : false;

  return (
    <div className="games-home" id="games-home">
      <h2 className="games-title" id="games-title">
        Games
      </h2>

      {recc && (
        <div className="games-recc" id="games-recc" data-test="games-recc" data-waiting={String(waitingName)}>
          <p className="games-recc-label">Pear recommends</p>
          <p className="games-recc-name" id="games-recc-name">
            {recc.title}
            {waitingName && (
              <span className="games-waiting" id="games-recc-waiting">
                Waiting for you
              </span>
            )}
          </p>
          <p className="games-recc-blurb">{recc.blurb}</p>
          <Pressable
            className="pear-btn pear-btn-mustard games-recc-play"
            id="btn-games-recc-play"
            onClick={() => {
              setAutoStart(false);
              setIntroAuto(recc.category !== undefined);
              setIntro(recc.id);
            }}
          >
            {waitingName ? 'Take your turn' : 'Play'}
          </Pressable>
        </div>
      )}

      {GAME_SECTIONS.map(section => {
        /* A game that has been started lives in Pear recommends, not here
           (card aduRfcsTSaXW): a half-played game must never look fresh. */
        const tiles = section.games.filter(tile => !started[tile.id]);
        if (tiles.length === 0) return null;
        return (
          <section className="games-section" id={`games-section-${section.key}`} key={section.key}>
            <h3 className="games-section-title">{section.title}</h3>
            <p className="games-section-copy">{section.copy}</p>
            <div className="games-grid">
              {tiles.map(tile => (
                <button
                  key={tile.id}
                  type="button"
                  className="games-tile"
                  id={`tile-${tile.id}`}
                  data-test={`tile-${tile.id}`}
                  onClick={() => {
                    setAutoStart(false);
                    setIntroAuto(false);
                    setIntro(tile.id);
                  }}
                >
                  <span className="games-tile-title">
                    {tile.title}
                    {waiting[tile.id] && <span className="games-waiting">Waiting for you</span>}
                  </span>
                  <span className="games-tile-blurb">{tile.blurb}</span>
                </button>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
